import { KeywordHighlightConfig } from "../../../components";
import { ColorInfo } from "../../../helpers";
import {
  EditableSynonymGroup,
  EditableSynonymSubgroup,
  Synonym,
  SynonymGroupDbData,
  SynonymUpdateDbData,
} from "../../../types";
import { SearchKeywordType } from "../../VirtualFolderPage/model/DocumentItemModel";

/**
 * 同義語の基本語彙でグルーピングする（Mapオブジェクト化）
 */
const groupSynonymsByWord = (synonyms: Synonym[]): Map<string, Synonym[]> => {
  const map = new Map<string, Synonym[]>();
  synonyms.forEach((synonym: Synonym) => {
    const arr = map.get(synonym.word);
    if (arr) {
      // 既存の配列に追加する
      arr.push(synonym);
    } else {
      // 配列を新規に追加する
      map.set(synonym.word, [synonym]);
    }
  });
  return map;
};

/**
 * グループ初期データ作成
 */
const initGroup = (
  index: number,
  dbGroup: SynonymGroupDbData,
  expand: boolean
): EditableSynonymGroup => {
  return {
    index,
    baseword: dbGroup.baseword,
    synonyms: dbGroup.synonyms.map((synonym: Synonym) => ({ ...synonym })),
    editableSubgroups: [],
    expand,
    editText: "",
    editing: false,
  };
};

/**
 * 同義語グループID収集
 */
const collectSynonymGroupIds = (group: EditableSynonymGroup): number[] => {
  let ids: number[] = [];
  group.editableSubgroups.forEach((subgroup: EditableSynonymSubgroup) => {
    const synonymGroupIds: number[] = subgroup.synonyms.map(
      (synonym: Synonym) => synonym.synonym_group_id
    );
    ids = ids.concat(synonymGroupIds);
  });
  // 重複を排除する（順序は保持される）
  return Array.from(new Set(ids));
};

/**
 * 状態が変更（削除, 追加）された同義語が存在するか？
 */
const hasEditedSynonyms = (groups: EditableSynonymGroup[]): boolean => {
  const editedGroups = groups.filter((group: EditableSynonymGroup) => {
    const editedSubgroups = group.editableSubgroups.filter(
      (subgroup: EditableSynonymSubgroup) =>
        subgroup.adding || subgroup.deleting
    );
    return editedSubgroups.length > 0;
  });
  return editedGroups.length > 0;
};

/**
 * 追加編集中（テキストフィールドが開いている）の同義語が存在するか？
 */
const isEditingSynonymAny = (groups: EditableSynonymGroup[]): boolean => {
  return (
    groups.filter((group: EditableSynonymGroup) => group.editing).length > 0
  );
};

/**
 * 検索キーワードヘルパー
 */
export const SearchKeywordHelper = {
  /**
   * 検索キーワードをフラットにして取得する
   */
  flattenSearchKeywords: (
    searchKeywords: SearchKeywordType[],
    unique?: boolean
  ): string[] => {
    // 検索キーワードをフラットに展開する
    let flatKeywords = searchKeywords
      .map((searchKeyword: SearchKeywordType) => searchKeyword.tokens)
      .flat();
    if (unique) {
      // 重複を除去する（順序は保持される）
      flatKeywords = Array.from(new Set(flatKeywords));
    }

    return flatKeywords;
  },
  /**
   * 同義語グループDBデータリストを編集可能グループリストに変換する
   */
  toEditableSearchKeywords: (
    dbGroups: SynonymGroupDbData[],
    expand: boolean
  ): EditableSynonymGroup[] => {
    const groups = [] as EditableSynonymGroup[];

    // グループ毎に処理する
    dbGroups.forEach((dbGroup: SynonymGroupDbData, index: number) => {
      // 同義語グループDBデータから編集可能グループを作成する
      const group = initGroup(index, dbGroup, expand);
      groupSynonymsByWord(dbGroup.synonyms).forEach((synonyms, word) => {
        // 基本語彙以外の同義語彙を追加する
        if (word !== dbGroup.baseword) {
          group.editableSubgroups.push({ word, synonyms });
        }
      });
      groups.push(group);
    });

    return groups;
  },
  /**
   * 同義語展開フラグの更新
   */
  updateGroup: (
    groups: EditableSynonymGroup[],
    target: EditableSynonymGroup,
    expand: boolean,
    editText: string,
    editing: boolean
  ): EditableSynonymGroup[] => {
    return groups.map((group: EditableSynonymGroup) => {
      if (group.baseword === target.baseword) {
        return {
          ...group,
          expand,
          editText,
          editing,
        };
      }
      return group;
    });
  },
  /**
   * 同義語の追加予約
   */
  updateAdding: (
    groups: EditableSynonymGroup[],
    target: EditableSynonymGroup
  ): EditableSynonymGroup[] => {
    return groups.map((group: EditableSynonymGroup, index: number) => {
      // 追加対象のグループ以外はそのまま返却する
      if (index !== target.index) {
        return {
          ...group,
          editableSubgroups: group.editableSubgroups.map(x => ({ ...x })), // コピー
        };
      }

      const subgroups: EditableSynonymSubgroup[] = group.editableSubgroups.map(
        x => ({ ...x })
      );
      // グループ内で同一の語彙を持つサブグループを取得し、
      // そこから同義語IDをすべて収集する
      const synonymGroupIds: number[] = collectSynonymGroupIds(group);
      if (synonymGroupIds.length === 0) {
        // 同義語が１つも設定されていないキーワードに同義語を追加するケース
        // 同義語グループも合せて新規作成する必要がある
        subgroups.push({
          word: group.editText.trim(),
          synonyms: [
            {
              id: 0, // 新規なので 0 固定
              synonym_group_id: 0, // 新規なので 0 固定
              word: group.editText.trim(),
            },
          ],
          deleting: false,
          adding: true,
        });
      } else {
        // 同義語サブグループリストを構築する
        subgroups.push({
          word: group.editText.trim(),
          // 既存の同義語グループID毎にsynonymsを追加する
          synonyms: synonymGroupIds.map((synonymGroupId: number) => ({
            id: 0, // 新規なので 0 固定
            synonym_group_id: synonymGroupId,
            word: group.editText.trim(),
          })),
          deleting: false,
          adding: true,
        });
      }

      // 同義語グループを構築して返却する
      return {
        ...group,
        editableSubgroups: subgroups,
        // 編集状態をクリアする
        editText: "",
        editing: false,
      } as EditableSynonymGroup;
    });
  },
  /**
   * 同義語の削除予約
   * 追加された同義語が削除された場合はデータ毎削除する
   */
  updateDeleting: (
    groups: EditableSynonymGroup[],
    target: EditableSynonymSubgroup,
    deleting: boolean
  ): EditableSynonymGroup[] => {
    return groups.map((group: EditableSynonymGroup) => {
      return {
        ...group,
        editableSubgroups: group.editableSubgroups
          .filter(
            (subgroup: EditableSynonymSubgroup) =>
              !(subgroup.word === target.word && subgroup.adding && deleting)
          )
          .map((subgroup: EditableSynonymSubgroup) => {
            return {
              ...subgroup,
              deleting:
                subgroup.word === target.word ? deleting : subgroup.deleting,
            };
          }),
      };
    });
  },
  /**
   * 同義語編集可能コンポーネントの操作モードを取得する
   */
  getSynonymEditableComponentControlMode: (
    groups: EditableSynonymGroup[],
    updateStatus: "ready" | "waiting" | "doing" | "done",
    updateResult?: "succeeded" | "failed"
  ): "normal" | "invisible" | "disabled" | "doing" | "done" => {
    let mode:
      | "normal"
      | "invisible"
      | "disabled"
      | "doing"
      | "done" = "invisible" as "invisible";
    if (updateStatus === "doing") {
      // 更新中
      mode = "doing" as "doing";
    } else if (updateStatus === "done" && updateResult === "succeeded") {
      // 更新成功
      mode = "done" as "done";
    } else if (isEditingSynonymAny(groups)) {
      // 編集中
      mode = "disabled" as "disabled";
    } else if (hasEditedSynonyms(groups)) {
      // 変更あり
      mode = "normal" as "normal";
    }
    return mode;
  },
  /**
   * 編集内容を元に戻す
   */
  resetEditedSynonyms: (
    groups: EditableSynonymGroup[]
  ): EditableSynonymGroup[] => {
    return groups.map((group: EditableSynonymGroup) => {
      return {
        ...group,
        editableSubgroups: group.editableSubgroups
          // 追加された同義語は無視する
          .filter((editableSubgroup: EditableSynonymSubgroup) => {
            return !editableSubgroup.adding;
          })
          // 追加,削除ステータスともにfalseを設定したサブグループを取得する
          .map((editableSubgroup: EditableSynonymSubgroup) => {
            return {
              ...editableSubgroup,
              deleting: false,
              adding: false,
            };
          }),
      };
    });
  },
  /**
   * キーワードハイライター設定作成
   *
   */
  getKeywordHighlightConfigs: (
    groups: EditableSynonymGroup[],
    getColor: (index: number) => ColorInfo
  ) => {
    const configs: KeywordHighlightConfig[] = [];
    groups.forEach((group: EditableSynonymGroup, index: number) => {
      // 基本語彙
      configs.push({
        keyword: group.baseword,
        backgroundColor: getColor(index).rgbaThick,
      });
      // 同義語彙
      if (group.editableSubgroups.length > 0) {
        group.editableSubgroups.forEach(
          (editableSynonymSubgroup: EditableSynonymSubgroup) => {
            configs.push({
              keyword: editableSynonymSubgroup.word,
              backgroundColor: getColor(index).rgbaThin,
            });
          }
        );
      }
    });
    return configs;
  },
  /**
   * 同義語更新DBデータリスト取得
   */
  getSynonymUpdateDbData: (
    groups: EditableSynonymGroup[]
  ): SynonymUpdateDbData[] => {
    // DB更新データ
    const dbData: SynonymUpdateDbData[] = [];

    // グループ毎に処理する
    groups.forEach((group: EditableSynonymGroup) => {
      // 既存の同義語グループに該当しない同義語リスト
      const orphans: Synonym[] = [];

      group.editableSubgroups.forEach((subgroup: EditableSynonymSubgroup) => {
        if (subgroup.deleting) {
          // 削除データ
          subgroup.synonyms.forEach((synonym: Synonym) => {
            dbData.push({
              synonym_id: synonym.id,
              synonym_group_id: synonym.synonym_group_id,
              words: [synonym.word],
              is_added: false,
              is_deleted: true,
            });
          });
        } else if (subgroup.adding) {
          // 追加データ
          subgroup.synonyms.forEach((synonym: Synonym) => {
            if (synonym.synonym_group_id === 0) {
              orphans.push(synonym);
            } else {
              dbData.push({
                synonym_id: synonym.id,
                synonym_group_id: synonym.synonym_group_id,
                words: [synonym.word],
                is_added: true,
                is_deleted: false,
              });
            }
          });
        }
      });

      // 新規同義語グループの更新データ作成
      if (orphans.length > 0) {
        dbData.push({
          synonym_id: null,
          synonym_group_id: null,
          // 基本語彙＋新規追加語彙を結合する
          words: [group.baseword].concat(
            orphans.map((synonym: Synonym) => synonym.word)
          ),
          is_deleted: false,
          is_added: true,
        });
      }
    });

    return dbData;
  },
};
